import React from 'react';
import _ from 'lodash';

import {
  styled,
  Button,
  Grid
} from '@mui/material';
import {
  AddAuditLogEventModal,
  BaseWidget,
  PermissionWall,
  ReceiveReturnAuthorizationModal
} from 'modules/layer01';

import { modalUtil } from 'iron-forge-react-modules';
import * as dataService from './dataService';



const ButtonContainerGrid = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(1)
}));


export interface IProps {
  storeId: string;
  returnAuthorizationId: string;

  onDeleteReturnAuthorization?: () => void;
}

export const FC: React.FC<IProps> = props => {
  const tuple = dataService.useQuery(props.returnAuthorizationId);
  const [
    acceptReturnAuthorization,
    acceptReturnAuthorizationTuple
  ] = dataService.useAcceptReturnAuthorization(props.returnAuthorizationId);
  const [
    initiateRefund,
    initiateRefundTuple
  ] = dataService.useInitiateRefund(props.returnAuthorizationId);
  const [
    generateReturnLabel,
    generateReturnLabelTuple
  ] = dataService.useGenerateReturnLabel(props.returnAuthorizationId);
  const [
    approveReturnAuthorization,
    approveReturnAuthorizationTuple
  ] = dataService.useApproveReturnAuthorization(props.returnAuthorizationId);
  const [
    reverseApprovalDecision,
    reverseApprovalDecisionTuple
  ] = dataService.useReverseReturnAuthorizationApprovalDecision(props.returnAuthorizationId);
  const [
    reverseAcceptanceDecision,
    reverseAcceptanceDecisionTuple
  ] = dataService.useReverseReturnAuthorizationAcceptanceDecision(props.returnAuthorizationId);
  const [
    reverseRefundDenialDecision,
    reverseRefundDenialDecisionTuple
  ] = dataService.useReverseReturnAuthorizationRefundDenialDecision(props.returnAuthorizationId);
  const [
    deleteReturnAuthorization,
    deleteReturnAuthorizationTuple
  ] = dataService.useDeleteReturnAuthorization(props.returnAuthorizationId, tuple.data.orderId);
  const {
    modalType2IsOpen,
    setModalIsOpen
  } = modalUtil.useModalTrackers([
    'addAuditLogEvent',
    'itemReceipt'
  ]);



  function handleClickAddNote() {
    setModalIsOpen('addAuditLogEvent', true);
  }

  function handleCloseAddAuditLogEventModal() {
    setModalIsOpen('addAuditLogEvent', false);
  }

  function handleClickGenerateLabel() {
    if (window.confirm("Are you sure you want to generate a return label? This will purchase a return label via EasyPost")) {
      generateReturnLabel();
    }
  }

  function handleClickDenyApproval() {
    approveReturnAuthorization('denied');
  }

  function handleClickApproveApproval() {
    approveReturnAuthorization('approved');
  }

  function handleClickDenyAcceptance() {
    if (window.confirm("Are you sure you want to deny this return? This action cannot be undone.")) {
      acceptReturnAuthorization('denied');
    }
  }

  function handleClickApproveAcceptance() {
    if (window.confirm("Are you sure you want to accept this return? This action cannot be undone.")) {
      acceptReturnAuthorization('approved');
    }
  }

  function handleClickReceiveItems() {
    setModalIsOpen('itemReceipt', true);
  }

  function handleCloseReceiveReturnAuthorizationModal() {
    setModalIsOpen('itemReceipt', false);
  }

  function handleClickDenyRefund() {
    if (window.confirm("Are you sure you want to deny this refund? This action cannot be undone.")) {
      initiateRefund('denied');
    }
  }

  function handleClickApproveRefund() {
    if (window.confirm("Are you sure you want to approve this refund? This action cannot be undone.")) {
      initiateRefund('approved');
    }
  }

  function handleClickShowLabel() {
    window.open(tuple.data.returnLabelUrl, '_blank');
  }

  function handleClickUndoAuthorization() {
    if (window.confirm("Are you sure you want to reverse the authorization decision?")) {
      reverseApprovalDecision();
    }
  }

  function handleClickUndoAcceptance() {
    if (window.confirm("Are you sure you want to reverse the acceptance decision?")) {
      reverseAcceptanceDecision();
    }
  }

  function handleClickUndoRefundDenial() {
    if (window.confirm("Are you sure you want to reverse the refundDenial decision?")) {
      reverseRefundDenialDecision();
    }
  }

  function handleClickDeleteAuthorization() {
    const str = "This will delete the authorization. This action is irreversible. Only click this button if you know exactly what you're doing.";
    if (window.confirm(str)) {
      deleteReturnAuthorization();

      if (props.onDeleteReturnAuthorization) {
        props.onDeleteReturnAuthorization();
      }
    }
  }



  const loading = _.some([
    tuple.loading,
    acceptReturnAuthorizationTuple.loading,
    initiateRefundTuple.loading,
    generateReturnLabelTuple.loading,
    approveReturnAuthorizationTuple.loading,
    reverseApprovalDecisionTuple.loading,
    reverseAcceptanceDecisionTuple.loading,
    reverseRefundDenialDecisionTuple.loading,
    deleteReturnAuthorizationTuple.loading
  ]);



  return tuple.called && !tuple.loading ? (
    <>
      <BaseWidget.FC>
        <Grid container spacing={2}>
          <ButtonContainerGrid item xs={2}>
            <PermissionWall.FC
              storeId={props.storeId}
              requiresPermissions={['returnAuthorization_addAuditLogEvent']}>
              <Button
                disabled={!tuple.called || loading}
                variant="contained"
                onClick={handleClickAddNote}>
                Add Note
              </Button>
            </PermissionWall.FC>

            {tuple.data.canDeleteReturnAuthorization && (
              <PermissionWall.FC
                storeId={props.storeId}
                requiresPermissions={['returnAuthorization_delete']}>
                <Button
                  color="error"
                  variant="contained"
                  onClick={handleClickDeleteAuthorization}>
                  Delete
                </Button>
              </PermissionWall.FC>
            )}
          </ButtonContainerGrid>

          <ButtonContainerGrid item xs={3}>
            {/* This is the authorization status */}
            {tuple.data.approvalStatus === 'pending' ? (
              <PermissionWall.FC
                storeId={props.storeId}
                requiresPermissions={['returnAuthorization_approval']}>
                <Button
                  disabled={loading}
                  color="warning"
                  variant="contained"
                  onClick={handleClickDenyApproval}>
                  Deny Authorization
                </Button>

                <Button
                  disabled={loading}
                  variant="contained"
                  onClick={handleClickApproveApproval}>
                  Approve Authorization
                </Button>
              </PermissionWall.FC>

            ) : (
              !tuple.data.hasUnreceivedItems && (
                <PermissionWall.FC
                  storeId={props.storeId}
                  requiresPermissions={['returnAuthorization_undoApproval']}>
                  <Button
                    disabled={loading}
                    color="warning"
                    onClick={handleClickUndoAuthorization}>
                    Undo Authorization Decision
                  </Button>
                </PermissionWall.FC>
              )
            )}
          </ButtonContainerGrid>

          <ButtonContainerGrid item xs={2}>
            {tuple.data.approvalStatus === 'approved' && tuple.data.canGenerateLabel && (
              tuple.data.returnLabelUrl ? (
                <PermissionWall.FC
                  storeId={props.storeId}
                  requiresPermissions={['returnAuthorization_downloadLabel']}>
                  <Button
                    disabled={loading}
                    variant="contained"
                    onClick={handleClickShowLabel}>
                    Show Label
                  </Button>
                </PermissionWall.FC>

              ) : (
                <PermissionWall.FC
                  storeId={props.storeId}
                  requiresPermissions={['returnAuthorization_generateLabel']}>
                  <Button
                    disabled={loading}
                    variant="contained"
                    onClick={handleClickGenerateLabel}>
                    Generate Label
                  </Button>
                </PermissionWall.FC>
              )
            )}

            {tuple.data.acceptanceStatus === 'pending' && tuple.data.approvalStatus === 'approved' && (
              <PermissionWall.FC
                storeId={props.storeId}
                requiresPermissions={['itemReceipt_create']}>
                <Button
                  disabled={tuple.data.approvalStatus !== 'approved' || loading}
                  variant="contained"
                  onClick={handleClickReceiveItems}>
                  {tuple.data.hasItemReceipt ? "Edit Receipt" : "Receive Items"}
                </Button>
              </PermissionWall.FC>
            )}
          </ButtonContainerGrid>

          <ButtonContainerGrid item xs={3}>
            {tuple.data.acceptanceStatus === 'pending' ? (
              <PermissionWall.FC
                storeId={props.storeId}
                requiresPermissions={['returnAuthorization_acceptance']}>
                <Button
                  disabled={loading || !tuple.data.hasItemReceipt}
                  color="warning"
                  variant="contained"
                  onClick={handleClickDenyAcceptance}>
                  Deny Return
                </Button>

                <Button
                  disabled={loading || !tuple.data.hasItemReceipt}
                  variant="contained"
                  onClick={handleClickApproveAcceptance}>
                  Accept Return
                </Button>
              </PermissionWall.FC>

            ) : tuple.data.refundStatus === 'pending' && (
              <PermissionWall.FC
                storeId={props.storeId}
                requiresPermissions={['returnAuthorization_acceptance']}>
                <Button
                  disabled={loading}
                  color="warning"
                  onClick={handleClickUndoAcceptance}>
                  Undo Acceptance Decision
                </Button>
              </PermissionWall.FC>
            )}
          </ButtonContainerGrid>

          <ButtonContainerGrid item xs={2}>
            {tuple.data.acceptanceStatus !== 'denied' && tuple.data.refundStatus === 'pending' ? (
              <PermissionWall.FC
                storeId={props.storeId}
                requiresPermissions={['refund_approval']}>
                <Button
                  disabled={loading || !tuple.data.hasUnreceivedItems || tuple.data.acceptanceStatus !== 'approved'}
                  variant="contained"
                  onClick={handleClickDenyRefund}>
                  Deny Refund
                </Button>

                <Button
                  disabled={loading || !tuple.data.hasUnreceivedItems || tuple.data.acceptanceStatus !== 'approved'}
                  variant="contained"
                  onClick={handleClickApproveRefund}>
                  Approve Refund
                </Button>
              </PermissionWall.FC>

            ) : tuple.data.refundStatus === 'denied' && (
              <PermissionWall.FC
                storeId={props.storeId}
                requiresPermissions={['refund_undoDenial']}>
                <Button
                  disabled={loading}
                  color="warning"
                  onClick={handleClickUndoRefundDenial}>
                  Undo Refund Denial
                </Button>
              </PermissionWall.FC>
            )}
          </ButtonContainerGrid>
        </Grid>
      </BaseWidget.FC>

      {modalType2IsOpen.addAuditLogEvent && (
        <AddAuditLogEventModal.FC
          returnAuthorizationId={props.returnAuthorizationId}
          onClose={handleCloseAddAuditLogEventModal} />
      )}

      {modalType2IsOpen.itemReceipt && (
        <ReceiveReturnAuthorizationModal.FC
          returnAuthorizationId={props.returnAuthorizationId}
          onClose={handleCloseReceiveReturnAuthorizationModal} />
      )}
    </>
  ) : null;
};
